import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "../styles/Login.css";
import { 
  TextField, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link as MuiLink
} from '@mui/material';
import { 
  LockReset,
  CheckCircle,
  Visibility,
  VisibilityOff,
  ArrowBack,
  ErrorOutline
} from '@mui/icons-material';
import { API_URL } from '../config';

const ResetPassword = () => {
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(API_URL + `/account/verify-reset-token/${token}`);
        const data = await response.json();
        setTokenValid(data.valid);
        if (!data.valid) {
          setMessage({ type: "error", text: data.error || "Lien invalide ou expiré." });
        }
      } catch {
        setMessage({ type: "error", text: "Erreur de connexion au serveur." });
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword.length < 6) {
      return setMessage({ type: "error", text: "Le mot de passe doit contenir au moins 6 caractères." });
    }
    if (newPassword !== confirmPassword) {
      return setMessage({ type: "error", text: "Les mots de passe ne correspondent pas." });
    }

    setLoading(true);

    try {
      const response = await fetch(API_URL + "/account/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setMessage({ type: "success", text: data.message });
      } else {
        setMessage({ type: "error", text: data.error });
      }
    } catch {
      setMessage({ type: "error", text: "Erreur de connexion au serveur." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="login-page">
      <Container maxWidth="sm" sx={{ paddingTop: '120px', paddingBottom: '60px' }}>
        <Paper 
          elevation={8}
          className="login-card"
          sx={{
            padding: 4,
            background: 'linear-gradient(145deg, rgba(26, 10, 46, 0.9) 0%, rgba(20, 25, 45, 0.9) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(156, 39, 176, 0.2)',
            borderRadius: '20px'
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
            <LockReset 
              sx={{ 
                fontSize: 60, 
                color: '#9C27B0',
                marginBottom: 2
              }} 
            />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #9C27B0 0%, #E1BEE7 50%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 1
              }}
            >
              Nouveau mot de passe
            </Typography>
          </Box>

          {verifying ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#9C27B0' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 2 }}>
                Vérification du lien...
              </Typography>
            </Box>
          ) : !tokenValid ? (
            <Box sx={{ textAlign: 'center' }}>
              <ErrorOutline sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
              {message && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {message.text}
                </Alert>
              )}
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                Ce lien est invalide ou a expiré. Veuillez refaire une demande de réinitialisation.
              </Typography>
              <Button
                variant="outlined"
                component={Link}
                to="/forgot-password"
                sx={{ 
                  color: '#9C27B0', 
                  borderColor: 'rgba(156,39,176,0.4)',
                  '&:hover': {
                    borderColor: '#9C27B0',
                    background: 'rgba(156,39,176,0.1)'
                  }
                }}
              >
                Refaire une demande
              </Button>
            </Box>
          ) : success ? (
            <Box sx={{ textAlign: 'center' }}>
              {message && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<CheckCircle />}
                >
                  {message.text}
                </Alert>
              )}
              <Button
                variant="contained"
                component={Link}
                to="/login"
                startIcon={<ArrowBack />}
                sx={{
                  background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
                  fontWeight: 700,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7B1FA2 0%, #512DA8 100%)',
                  }
                }}
              >
                Se connecter
              </Button>
            </Box>
          ) : (
            <>
              {message && (
                <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }}>
                  {message.text}
                </Alert>
              )}
              <form onSubmit={handleSubmit}>
                <TextField 
                  label="Nouveau mot de passe"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ marginBottom: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9C27B0',
                      }
                    }
                  }}
                />

                <TextField 
                  label="Confirmer le mot de passe"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ marginBottom: 3 }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.3)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(156, 39, 176, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9C27B0',
                      }
                    }
                  }}
                />

                <Button 
                  variant="contained" 
                  type="submit" 
                  fullWidth 
                  size="large"
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    padding: '14px',
                    fontSize: '1.05rem',
                    marginBottom: 3,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7B1FA2 0%, #512DA8 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(156, 39, 176, 0.4)'
                    },
                    '&:disabled': {
                      background: 'rgba(156, 39, 176, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Réinitialiser le mot de passe"}
                </Button>
              </form>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
